import * as React from "react"
import { Waypoint } from "react-waypoint"
import Loading from "@modules/ui/components/Loading"
import { buildLogs as text } from "@modules/locales/default.json"
import { useBuildRawLogs } from "../hooks/useBuildRawLogs"
import { EmptyState, ThemeCss, RawLogs } from "gatsby-interface"
import { EmptyStateGraphic } from "@modules/ui/components/EmptyStateGraphic"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { NetworkStatus } from "apollo-client"

const headerCss: ThemeCss = theme => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontFamily: theme.fonts.heading,
  fontSize: theme.fontSizes[5],
  fontWeight: theme.fontWeights.semiBold,
  padding: theme.space[7],
  paddingRight: theme.space[8],
})

const loadingCss: ThemeCss = theme => ({
  marginTop: theme.space[5],
  marginBottom: theme.space[5],
})

export function BuildRawLogsSectionHeader({
  children,
}: {
  children?: React.ReactNode
}) {
  return <header css={headerCss}>{children || text.headers.previewLogs}</header>
}

export type BuildRawLogsListProps = {
  buildId: string
  siteId: string
}

export function BuildRawLogsList({ buildId, siteId }: BuildRawLogsListProps) {
  const [
    logs,
    { loading, error, loadMoreLogs, networkStatus },
  ] = useBuildRawLogs(buildId, siteId)

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading && networkStatus !== NetworkStatus.fetchMore) {
    return <Loading delay={1000} />
  }

  if (logs.length === 0) {
    return (
      <EmptyState
        graphic={<EmptyStateGraphic />}
        heading={text.headers.noRawLogs}
        text=""
        variant="WITH_BACKGROUND"
      />
    )
  }

  return (
    <React.Fragment>
      <RawLogs logItems={logs} aria-label="Raw logs" />
      <Waypoint onEnter={loadMoreLogs} />
      {loading && networkStatus === NetworkStatus.fetchMore ? (
        <Loading variant="baby" css={loadingCss} />
      ) : null}
    </React.Fragment>
  )
}
